import PouchDB from '@/repos/PouchDB'
import type { SyncStatusType, SyncType } from '@/types'
import { SyncPouchRepo } from '@/repos/SyncPouchRepo'
import HamrsDotAppService, { HamrsSyncBadApiKeyError } from '@/services/HamrsDotApp'
import { captureException, setSyncIdentity } from '@/utils/sentry'

let syncHandler: null | ReturnType<typeof PouchDB.sync> = null
let changeListener: null | ReturnType<typeof PouchDB.changes> = null
let lastApiKey: string = ''
const inactiveSubscriptionErrorMessage = 'Sync Paused: hamrs.app subscription is inactive. Check your subscription, or remove your sync key.'
let statusCheckTimeout: NodeJS.Timeout | null = null
let liveSyncCallback: ((changes: any) => void) | null = null

const statusCheckInterval = 1000 * 60 * 60 * 24

// Retry state for transient sync-configuration failures (e.g. a network blip
// during the initial pull). Capped exponential backoff so sync self-heals
// without hammering the server. Reset on a clean configure pass.
//
// Network failures track their own counter: they retry indefinitely (a device
// can start offline and stay offline for hours; sync should come back on its
// own once connectivity returns) and they must not consume the real-error
// budget -- otherwise an offline burst at startup leaves a later, genuinely
// transient error (e.g. iOS's empty-message IndexedDB "connection is closing"
// failure after the app resumes) with zero retries, reporting it to Sentry on
// its first occurrence (HAMRS-15).
let retryTimeout: ReturnType<typeof setTimeout> | null = null
let configureRetryCount = 0
let networkRetryCount = 0
const maxConfigureRetries = 5
const retryBaseDelayMs = 1000 * 5
// +/- 25% randomized jitter so many clients that failed at the same instant
// (e.g. a brief server outage) don't all retry in lockstep.
const retryJitterFactor = 0.5

// A rejected fetch (no response received) surfaces with an engine-specific
// message: "Failed to fetch" (Blink), "Load failed" (WebKit/iOS), "NetworkError
// when attempting to fetch resource" (Firefox), "Network request failed" (RN).
// These are transient client-network conditions (offline, dropped connection,
// app backgrounded mid-request), not bugs -- distinct from a fetch that resolves
// with a bad HTTP status, which we still treat as a real error.
//
// We match on the MESSAGE, not the constructor: a raw fetch rejects with a
// TypeError, but PouchDB (the initial-pull replication) rewraps the same failure
// as a plain Error or a `{ message }` object, so an `instanceof TypeError` check
// would miss those.
function isNetworkError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : typeof (error as { message?: unknown })?.message === 'string'
        ? (error as { message: string }).message
        : ''
  return /load failed|failed to fetch|networkerror|network request failed/i.test(message)
}

const SyncService = {
  find(): Promise<SyncType> {
    return SyncPouchRepo.find()
  },
  update(sync: Partial<SyncType>): Promise<SyncType> {
    return SyncPouchRepo.update(sync)
  },

  // configureSync checks if the API key stored in sync is valid and
  // enables PouchDB replication. It takes a callback function where
  // we report sync status so users can be notified if their subscription
  // lapses or the server fails etc.
  async configureSync(sync: SyncType, reportStatus: (status: SyncStatusType) => void) {
    if (statusCheckTimeout) {
      clearTimeout(statusCheckTimeout)
    }
    if (retryTimeout) {
      clearTimeout(retryTimeout)
      retryTimeout = null
    }

    // If they get rid of their API Key then just disable sync and move on.
    if (!sync?.hamrs.apiKey) {
      this.disableSync()
      configureRetryCount = 0
      networkRetryCount = 0
      reportStatus({
        lastSynced: null,
        valid: true,
        error: ''
      })
      return
    }

    // Track how far we got so a failure is reported to Sentry tagged with the
    // phase it died in (subscription-check vs initial-pull vs start-sync).
    let phase: 'subscription-check' | 'initial-pull' | 'start-sync' = 'subscription-check'
    let subscribed: boolean | undefined

    try {
      const subscription = await HamrsDotAppService.couchDbSubscription(sync.hamrs.apiKey)
      subscribed = subscription.subscribed

      if (!subscription.subscribed) {
        this.disableSync()
        reportStatus({
          lastSynced: null,
          valid: false,
          error: inactiveSubscriptionErrorMessage
        })
        // Try to turn it back on again later. This will probably just result in
        // toasting the user again tomorrow, but that's ok.
        statusCheckTimeout = setTimeout(() => {
          this.configureSync(sync, reportStatus)
        }, statusCheckInterval)
        return
      }

      if (!subscription.url) {
        throw new Error('HAMRS Sync gave a malformed URL')
      }

      // Tag every subsequent Sentry event with this account's CouchDB name so a
      // sync issue can be tied to the exact userdb. This identity comes over the
      // network, so it survives even a corrupt local database.
      setSyncIdentity(subscription.url)

      if (subscription.subscribed && lastApiKey != sync.hamrs.apiKey) {
        this.disableSync()
        lastApiKey = sync.hamrs.apiKey

        // Do a single 1-way pull from the remote db when we start up.
        phase = 'initial-pull'
        await PouchDB.replicate.from(subscription.url)

        // Start the normal PouchDB sync (this should always run when subscription is valid)
        phase = 'start-sync'
        syncHandler = PouchDB.sync(subscription.url, { live: true, retry: true })
        syncHandler
          .on('change', function () {
            // pouch: document changes detected
          })
          .on('paused', function () {
            // pouch: replication paused (e.g. replication up to date, user went offline)
            reportStatus({ lastSynced: new Date(), valid: true, error: '' })
          })
          .on('active', function () {
            // pouch: replication active (e.g. new changes replicating, user went back online)
          })
          .on('denied', function (err) {
            // pouch: a single document failed to replicate (e.g. permissions);
            // replication otherwise continues. In our couch-per-user setup the
            // user is not a db admin, so local-only index design docs
            // (_design/idx-*) can never be pushed -- that rejection is expected
            // and benign. CouchDB reports it with a _design/* doc id, or -- when
            // the doc id isn't surfaced in the denied payload -- with the generic
            // "not a db or server admin" reason. Suppress both on the PUSH side.
            // A _design/* denial while pulling is a real auth/replication problem
            // and must still surface, as is a denial we can attribute to an actual
            // user-data doc.
            const denied = err as { direction?: string; doc?: any }
            const docId: string | undefined = denied?.doc?._id
            // The 'denied' payload is a plain object, not an Error, so wrap it so
            // Sentry produces a meaningful title and groups sensibly, with the
            // document detail attached as context.
            const reason = denied?.doc?.reason ?? denied?.doc?.message ?? 'forbidden'

            const isExpectedPushDenial =
              denied?.direction === 'push' &&
              ((typeof docId === 'string' && docId.startsWith('_design/')) ||
                (docId === undefined && /not a db or server admin/i.test(reason)))
            if (isExpectedPushDenial) {
              return
            }

            captureException(new Error(`Sync denied for ${docId ?? 'unknown doc'}: ${reason}`), {
              phase: 'live-sync',
              event: 'denied',
              direction: denied?.direction,
              docId,
              reason,
              status: denied?.doc?.status,
            })
          })
          .on('error', function (err) {
            // pouch: ongoing replication error (network, conflict, server failure)
            captureException(err, { phase: 'live-sync', event: 'error' })
          })

        // We made it through the initial pull and started live sync, so clear
        // any accumulated retry backoff.
        configureRetryCount = 0
        networkRetryCount = 0

        // queue a thing to recheck their status periodically and turn sync off if their subscription has lapsed.
        statusCheckTimeout = setTimeout(() => {
          this.disableSyncIfSubscriptionLapses(sync, reportStatus)
        }, statusCheckInterval)
      }
    } catch (error) {
      this.disableSync()
      console.error('Error in configureSync:', error)

      const isBadKey = error instanceof HamrsSyncBadApiKeyError
      const isNetwork = isNetworkError(error)
      // A bad API key won't fix itself, so don't retry it. Network failures
      // retry indefinitely (with the backoff capped below): the device may
      // simply be offline, and sync should come back on its own whenever
      // connectivity returns. Everything else (transient server/replication or
      // PouchDB IndexedDB failure during the initial pull) gets a capped
      // exponential-backoff retry so sync self-heals.
      const willRetry = !isBadKey && (isNetwork || configureRetryCount < maxConfigureRetries)

      // Never report a bad API key: it's an expected user state (revoked or
      // regenerated key, or a lapsed subscription), surfaced to the user via the
      // reportStatus toast below. Never report a transient network failure: it's
      // offline/connection noise that heals on retry.
      //
      // For anything else -- including transient PouchDB/IndexedDB errors during
      // the initial pull (e.g. iOS "InvalidStateError: ... connection is closing",
      // which can even arrive with an empty message) -- report only once we've
      // stopped retrying. A failure that survives the full backoff is worth
      // surfacing; one that self-heals on the next attempt is just noise.
      if (!isBadKey && !isNetwork && !willRetry) {
        captureException(error, { phase, subscribed })
      }
      reportStatus({
        lastSynced: null,
        valid: false,
        error: error instanceof Error ? error.message : 'Failed to check subscription status'
      })

      if (willRetry) {
        // Network failures back off on their own counter, capped at the same
        // max exponent (~160s), so being offline for a long stretch settles
        // into a gentle poll instead of consuming the real-error budget.
        const backoffStep = isNetwork
          ? Math.min(networkRetryCount, maxConfigureRetries)
          : configureRetryCount
        const baseDelay = retryBaseDelayMs * 2 ** backoffStep
        const delay = baseDelay * (1 + (Math.random() - 0.5) * retryJitterFactor)
        if (isNetwork) {
          networkRetryCount++
        } else {
          configureRetryCount++
        }
        retryTimeout = setTimeout(() => {
          this.configureSync(sync, reportStatus)
        }, delay)
      }
    }
  },

  disableSync() {
    if (syncHandler) {
      syncHandler.cancel()
      syncHandler = null
    }
    if (changeListener) {
      changeListener.cancel()
      changeListener = null
    }
    if (statusCheckTimeout) {
      clearTimeout(statusCheckTimeout)
      statusCheckTimeout = null
    }
    if (retryTimeout) {
      clearTimeout(retryTimeout)
      retryTimeout = null
    }
    // Clear the live sync callback to prevent memory leaks and stale references
    liveSyncCallback = null
    lastApiKey = ''
  },

  enableLiveSync(callback: (changes: any) => void) {
    if (liveSyncCallback) return   // already registered
    liveSyncCallback = callback
    this.startChangeListener()
  },

  disableLiveSync() {
    liveSyncCallback = null
    if (changeListener) {
      changeListener.cancel()
      changeListener = null
    }
    // Note: We don't stop syncHandler here - that's the normal sync which should keep running
  },

  startChangeListener() {
    if (!liveSyncCallback || changeListener) return

    // Verify PouchDB is ready before starting listener
    if (!PouchDB || !PouchDB.changes) {
      console.error('PouchDB not available for change listener')
      return
    }

    changeListener = PouchDB.changes({
      since: 'now',
      live: true,
      include_docs: true,
      filter: (doc: any) => {
        // Only listen for Logbook changes - these occur when QSOs are added/deleted
        return doc._id.startsWith('LOGBOOK:')
      }
    })
      .on('change', (change) => {
        if (liveSyncCallback && change.doc) {
          liveSyncCallback(change)
        }
      })
      .on('error', (err) => {
        console.error('PouchDB change listener error:', err)
      })
  },

  async disableSyncIfSubscriptionLapses(
    sync: SyncType,
    reportStatus: (status: SyncStatusType) => void
  ) {
    const subscription = await HamrsDotAppService.couchDbSubscription(sync.hamrs.apiKey)
    if (!subscription.subscribed) {
      // Whoops! They'd only be in this function if they had sync turned on
      // and working, so if their subscription is returning false then they expired.
      this.disableSync()
      reportStatus({
        lastSynced: null,
        valid: false,
        error: inactiveSubscriptionErrorMessage
      })
      // Try to turn it back on again later. This will probably just result in
      // toasting the user again tomorrow, but that's ok.
      statusCheckTimeout = setTimeout(() => {
        this.configureSync(sync, reportStatus)
      }, statusCheckInterval)
      return
    }

    // If they're here then they're still active. Check again in a minute.
    statusCheckTimeout = setTimeout(() => {
      this.disableSyncIfSubscriptionLapses(sync, reportStatus)
    }, statusCheckInterval)
  },

  async cancelSyncAndDestroy() {
    return new Promise<void>((resolve, reject) => {
      if (!syncHandler) {
        reject(new Error('syncHandler is null'))
        return
      }

      // Register a callback handler to wait for syncing to be complete.
      syncHandler.on('complete', async function () {
        // Data is done syncing, delete our local copy.
        await PouchDB.destroy()

        // We're done
        resolve()
      })

      // Tell pouch to stop syncing with our cloud db.
      syncHandler.cancel()
    }).catch((error) => {
      console.error('Error in cancelSyncAndDestroy:', error)
      throw error
    })
  }
}

export default SyncService
